import { useContext } from "react";
import type { backendReportSurgicalRowDTO } from "../../backend/target/typescript/dto";
import { RequestContext } from "../request-context";
import { requestGet, requireResult } from "../utils/network";
import { showBoolean } from "../utils/security";
import { asNumber } from "../utils/strings";
import { getPeriodRange } from "../widget/i-period";
import { Modes } from "../widget/io-abstract";
import IONumber from "../widget/io-number";
import SearchPanel, { getPref, type FormValues } from "../widget/search-panel";
import TablePanel from "../widget/table-panel";

type ServerResponse = backendReportSurgicalRowDTO[];

function ageAtReference(
  year_of_birth: number | string | undefined,
  reference: string
): string {
  if (!year_of_birth) {
    return "-";
  }

  const year =
    typeof year_of_birth === "number"
      ? year_of_birth
      : Number.parseInt(year_of_birth.substring(0, 4), 10);

  const refYear = Number.parseInt((reference ?? "").substring(0, 4), 10);
  if (!Number.isFinite(refYear)) {
    return "?";
  }

  return "" + Math.max(0, refYear - year);
}

export default function ReportSurgical(): React.ReactNode {
  const requestContext = useContext(RequestContext)!;
  const onSearch = (formData: FormValues): Promise<React.ReactNode> =>
    requestContext
      .fetchJSONAndMessage<ServerResponse>(
        "Generating report",
        requestGet("/api/reports/surgical", {
          ...formData,
          ...getPeriodRange("year", undefined, undefined, formData["year"])
        })
      )
      .then(requireResult)
      .then((list) => (
        <TablePanel
          rows={list}
          headers={
            <thead>
              <tr>
                <th></th>
                <th>Date</th>
                <th>Center</th>
                <th>Entry Number</th>
                <th>Name</th>
                <th>Age</th>
                <th>Sex</th>
                <th>Tk income</th>
                <th>Nb pers</th>
                <th>Tk per pers</th>
                <th>SL</th>
                <th>Pathology</th>
                <th>Last Seen</th>
                <th>Result</th>
                <th>Treatment Finished</th>
                <th>Consult</th>
                <th>Medicine</th>
                <th>Surgical</th>
                <th>Workshop</th>
                <th>Others</th>
                <th>Full</th>
                <th>Asked</th>
                <th>Paid</th>
              </tr>
            </thead>
          }
          lineLink={(row) =>
            `/patient/${row.patient_id}/patient/${row.patient_id}`
          }
          lineColumns={(row) => {
            const consult = asNumber(row.total_consult);
            const medecine = asNumber(row.total_medecine);
            const surgical = asNumber(row.total_surgical);
            const workshop = asNumber(row.total_workshop);
            const other = asNumber(row.total_other);
            const real = asNumber(row.total_real);
            const asked = asNumber(row.total_asked);
            const paid = asNumber(row.total_paid);

            const household = asNumber(
              row.social_level_number_of_household_members
            );
            const salary = asNumber(row.social_level_family_salary);

            return (
              <>
                <td>{row.last_bill_date ?? ""}</td>
                <td>{row.center ?? ""}</td>
                <td>{row.patient_reference ?? ""}</td>
                <td>{row.patient_name ?? ""}</td>
                <td>
                  {ageAtReference(
                    row.year_of_birth,
                    getPeriodRange(
                      "year",
                      undefined,
                      undefined,
                      formData["year"]
                    ).date_start
                  )}
                </td>
                <td>{row.sex ?? ""}</td>
                <td>{salary || ""}</td>
                <td>{household || ""}</td>
                <td>{household ? Math.round(salary / household) : ""}</td>
                <td>{row.social_level ?? ""}</td>
                <td>{row.pathology ?? ""}</td>
                <td>{row.last_seen ?? ""}</td>
                <td>{row.last_treat_result ?? ""}</td>
                <td>{showBoolean(row.last_treat_finished)}</td>
                <td>{consult || ""}</td>
                <td>{medecine || ""}</td>
                <td>{surgical || ""}</td>
                <td>{workshop || ""}</td>
                <td>{other || ""}</td>
                <td>{real || ""}</td>
                <td>{asked || ""}</td>
                <td>{paid || ""}</td>
              </>
            );
          }}
          footers={
            <tfoot>
              <tr>
                <th colSpan={15}>total</th>
                <th>
                  {list?.reduce(
                    (acc, row) => acc + asNumber(row.total_consult),
                    0
                  )}
                </th>
                <th>
                  {list?.reduce(
                    (acc, row) => acc + asNumber(row.total_medecine),
                    0
                  )}
                </th>
                <th>
                  {list?.reduce(
                    (acc, row) => acc + asNumber(row.total_surgical),
                    0
                  )}
                </th>
                <th>
                  {list?.reduce(
                    (acc, row) => acc + asNumber(row.total_workshop),
                    0
                  )}
                </th>
                <th>
                  {list?.reduce(
                    (acc, row) => acc + asNumber(row.total_other),
                    0
                  )}
                </th>
                <th>
                  {list?.reduce(
                    (acc, row) => acc + asNumber(row.total_real),
                    0
                  )}
                </th>
                <th>
                  {list?.reduce(
                    (acc, row) => acc + asNumber(row.total_asked),
                    0
                  )}
                </th>
                <th>
                  {list?.reduce(
                    (acc, row) => acc + asNumber(row.total_paid),
                    0
                  )}
                </th>
              </tr>
            </tfoot>
          }
          reportBasename={`surgical ${formData["year"]}`}
        />
      ));

  return (
    <div>
      <h1>
        <img src="/static/img/reports.gif" />
        Surgical Report
      </h1>

      <SearchPanel
        form={
          <>
            <IONumber
              name="year"
              mode={Modes.input}
              value={asNumber(
                getPref("year", new Date().getFullYear().toString())
              )}
              required
              htmlProps={{ min: 1990, max: 2100 }}
            />
          </>
        }
        onSearch={onSearch}
      />
    </div>
  );
}
